import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, useAuth } from "@/hooks/useAuth";
import ProfileDropdown from "@/components/kalki/ProfileDropdown";

/**
 * KalkiGlobalHeader — ONE persistent authenticated header mounted inside
 * each authenticated page shell. Renders:
 *   • Left: KALKI wordmark → /app
 *   • Center (desktop): Home · Chat · Talk · Future · Temple · My Journey
 *   • Right: credits badge · profile avatar
 *   • Mobile: compact row (wordmark + credits + avatar), hamburger reveals nav.
 */
const NAV_ITEMS = [
  { id: "home",    label: "Home",       to: "/app" },
  { id: "chat",    label: "Chat",       to: "/app/chat" },
  { id: "talk",    label: "Talk",       to: "/app/talk" },
  { id: "future",  label: "Future",     to: "/app/future" },
  { id: "temple",  label: "Temple",     to: "/app/temple" },
  { id: "journey", label: "My Journey", to: "/app/journey" },
];

export default function KalkiGlobalHeader() {
  const { user, logout } = useAuth() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [latestReportId, setLatestReportId] = useState(null);

  useEffect(() => {
    let dead = false;
    const load = async () => {
      try { const { data } = await api.get("/credits");
            if (!dead) setCredits(data?.balance ?? null); }
      catch (_) {}
    };
    load();
    const h = () => load();
    window.addEventListener("kalki:credits-topup-needed", h);
    window.addEventListener("kalki:credits-refresh", h);
    return () => {
      dead = true;
      window.removeEventListener("kalki:credits-topup-needed", h);
      window.removeEventListener("kalki:credits-refresh", h);
    };
  }, []);

  // Load latest completed Life Report id (for the "Download Latest Report" item)
  useEffect(() => {
    if (!user) { setLatestReportId(null); return; }
    let dead = false;
    const load = async () => {
      try {
        const { data } = await api.get("/report/library");
        if (dead) return;
        const reports = data?.reports || [];
        const latest = reports.find((r) =>
          (r.status || "").toLowerCase() === "completed" ||
          (r.status || "").toLowerCase() === "complete" ||
          (r.status || "").toLowerCase() === "done" ||
          r.pdf_url || r.ready
        ) || reports[0] || null;
        setLatestReportId(latest?.id || null);
      } catch (_) {}
    };
    load();
    const h = () => load();
    window.addEventListener("kalki:report-ready", h);
    return () => {
      dead = true;
      window.removeEventListener("kalki:report-ready", h);
    };
  }, [user]);

  const isActive = (to) => {
    if (to.startsWith("/app?tile=")) {
      const t = new URL(to, "http://x").searchParams.get("tile");
      return location.pathname === "/app" &&
             new URLSearchParams(location.search).get("tile") === t;
    }
    return location.pathname === to;
  };

  return (
    <header data-testid="kalki-global-header"
            style={{
              position: "sticky", top: 0, zIndex: 50,
              height: 72, display: "flex", alignItems: "center",
              padding: "0 20px",
              background: "rgba(255,255,255,0.9)",
              backdropFilter: "saturate(140%) blur(16px)",
              borderBottom: "1px solid rgba(0,0,0,0.05)",
            }}>
      <Link to="/app" data-testid="header-logo"
            style={{display: "flex", alignItems: "center", gap: 8,
                    textDecoration: "none"}}>
        <span style={{fontSize: 20, fontWeight: 500, letterSpacing: "0.14em",
                       color: "#1C1C1C"}}>KALKI</span>
      </Link>

      {/* Desktop nav */}
      <nav className="kalki-header-nav"
           style={{marginLeft: 40, display: "flex", gap: 24, flex: 1}}
           data-testid="header-nav">
        {NAV_ITEMS.map((it) => (
          <Link key={it.id} to={it.to}
                data-testid={`header-nav-${it.id}`}
                style={{
                  fontSize: 13, letterSpacing: "0.04em",
                  color: isActive(it.to) ? "#5D00FF" : "#434353",
                  textDecoration: "none", fontWeight: isActive(it.to) ? 500 : 400,
                }}>{it.label}</Link>
        ))}
      </nav>

      <div style={{marginLeft: "auto", display: "flex", alignItems: "center",
                   gap: 12}}>
        <span data-testid="header-credits"
              style={{fontSize: 12, color: "#434353", padding: "6px 12px",
                       borderRadius: 999, background: "rgba(93,0,255,0.08)",
                       border: "1px solid rgba(93,0,255,0.15)",
                       whiteSpace: "nowrap"}}>
          {credits !== null ? `${credits} Credits` : "— Credits"}
        </span>
        {user ? (
          <ProfileDropdown
            user={user}
            credits={credits}
            hasLatestReport={!!latestReportId}
            onOpenMe={() => navigate("/app?tile=me")}
            onOpenJourney={() => navigate("/app/journey")}
            onOpenReports={() => navigate("/app?tile=me")}
            onOpenWallet={() => {
              try { window.dispatchEvent(new CustomEvent("kalki:open-wallet")); } catch (_) {}
              if (location.pathname !== "/app") navigate("/app");
            }}
            onOpenReferAndEarn={() => {
              try {
                window.dispatchEvent(new CustomEvent("kalki:open-wallet"));
                window.dispatchEvent(new CustomEvent("kalki:wallet-focus-referral"));
              } catch (_) {}
              if (location.pathname !== "/app") navigate("/app");
            }}
            onOpenRedeemCode={() => {
              try { window.dispatchEvent(new CustomEvent("kalki:open-redeem-code")); } catch (_) {}
            }}
            onDownloadLatestReport={() => {
              if (!latestReportId) return;
              const base = process.env.REACT_APP_BACKEND_URL;
              const url = `${base}/api/report/${latestReportId}/download`;
              const a = document.createElement("a");
              a.href = url;
              a.setAttribute("download", `KALKI_Life_Report_${latestReportId.slice(0, 8)}.pdf`);
              a.setAttribute("target", "_blank");
              a.rel = "noopener";
              document.body.appendChild(a);
              a.click();
              a.remove();
            }}
            onOpenSettings={() => navigate("/app?tile=me")}
            onSwitchAccount={async () => {
              try { await (logout?.()); } catch (_) {}
              window.location.href = "/";
            }}
            onCreateNew={async () => {
              try { await (logout?.()); } catch (_) {}
              window.location.href = "/";
            }}
            onLogout={async () => {
              try { await (logout?.()); } catch (_) {}
              window.location.href = "/";
            }}
          />
        ) : (
          <Link to="/app?tile=me" data-testid="header-avatar"
                style={{display: "inline-flex", width: 36, height: 36,
                         borderRadius: 999,
                         background: "linear-gradient(135deg,#5D00FF,#FF2E9A)",
                         color: "white", alignItems: "center",
                         justifyContent: "center", fontSize: 13,
                         textDecoration: "none"}}>
            K
          </Link>
        )}
        <button data-testid="header-menu-btn"
                onClick={() => setMenuOpen((v) => !v)}
                className="kalki-header-menu-btn"
                aria-label="Open menu"
                style={{display: "none", marginLeft: 4, background: "none",
                         border: 0, color: "#434353", fontSize: 20,
                         cursor: "pointer", padding: 6}}>
          ☰
        </button>
      </div>

      {menuOpen && (
        <div data-testid="header-mobile-menu"
             onClick={() => setMenuOpen(false)}
             style={{position: "fixed", inset: 0, top: 72, zIndex: 60,
                      background: "rgba(0,0,0,0.3)"}}>
          <div onClick={(e) => e.stopPropagation()}
               style={{background: "white", padding: "16px 20px",
                        display: "flex", flexDirection: "column", gap: 2,
                        borderBottom: "1px solid rgba(0,0,0,0.05)"}}>
            {NAV_ITEMS.map((it) => (
              <Link key={it.id} to={it.to} onClick={() => setMenuOpen(false)}
                    data-testid={`header-mobile-${it.id}`}
                    style={{padding: "12px 4px", fontSize: 15,
                             color: isActive(it.to) ? "#5D00FF" : "#1C1C1C",
                             textDecoration: "none"}}>{it.label}</Link>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 820px) {
          .kalki-header-nav { display: none !important; }
          .kalki-header-menu-btn { display: inline-block !important; }
        }
      `}</style>
    </header>
  );
}
